import { Location } from "@angular/common";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { AgCartesianSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { ShippedInvoiceRevenue } from "app/model/entities/-chart-classes";

import { DateFns } from "app/shared/date-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import { UtilFns } from "app/shared/util-fns";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const DEFAULT_WEEKS_BACK = 12;

/** One bar + one point on the line: a Monday-to-Sunday week of shipping. */
interface WeekBar {
  weekStart: Date;
  weekLabel: string;
  revenueAmt: number;
  shipmentCount: number;
  unitCount: number;
}

@Component({
  selector: "app-chart-weekly-shipment-revenue",
  templateUrl: "./chart-weekly-shipment-revenue.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartWeeklyShipmentRevenueComponent extends DomainBaseComponent implements OnInit {
  options: any;
  weeksBack = DEFAULT_WEEKS_BACK;
  totalRevenueAmt = 0;
  totalShipmentCount = 0;
  revenuePerShipmentAmt = 0;

  readonly weeksOptions: { label: string; value: number }[] = [
    { label: "Last 6 Weeks", value: 6 },
    { label: "Last 12 Weeks", value: 12 },
    { label: "Last 18 Weeks", value: 18 },
    { label: "Last 24 Weeks", value: 24 },
  ];

  private detailRows: ShippedInvoiceRevenue[] = [];
  private barData: WeekBar[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.processChartData();
    this.isPageReady = true;
  }

  async onWeeksChange() {
    this.isPageReady = false;
    await this.processChartData();
    this.isPageReady = true;
  }

  private async processChartData() {
    const data = (await this.dbQueryService.getShippedInvoiceRevenues(this.weeksBack)) ?? [];
    DateFns.fixupDatesInProjection(data, ["weekStart", "shipDate"]);

    this.detailRows = _.orderBy(data, ["shipDate", "invoiceHeaderId"]);
    this.barData = this.buildBars(data);

    this.totalRevenueAmt = _.sumBy(this.barData, (b) => b.revenueAmt);
    this.totalShipmentCount = _.sumBy(this.barData, (b) => b.shipmentCount);
    this.revenuePerShipmentAmt =
      this.totalShipmentCount === 0 ? 0 : this.totalRevenueAmt / this.totalShipmentCount;

    if (this.barData.length === 0) {
      this.options = null;
      return;
    }

    this.options = {
      data: this.barData,
      series: [
        {
          type: "bar",
          xKey: "weekLabel",
          yKey: "revenueAmt",
          yName: "Shipment revenue",
          tooltip: { renderer: this.renderer.bind(this) },
        },
        {
          type: "line",
          xKey: "weekLabel",
          yKey: "shipmentCount",
          yName: "Number of shipments",
          marker: { enabled: true },
          tooltip: { renderer: this.renderer.bind(this) },
        },
      ],
      axes: [
        { type: "category", position: "bottom", title: { text: "Week Beginning (Monday)" } },
        {
          type: "number",
          position: "left",
          keys: ["revenueAmt"],
          title: { text: "Revenue" },
          label: { formatter: (p: any) => UtilFns.fmtCurrency(p.value) },
        },
        {
          type: "number",
          position: "right",
          keys: ["shipmentCount"],
          title: { text: "Shipments" },
        },
      ],
    };
  }

  /** Every week in the window gets a bar, including the ones nothing shipped in, so a quiet
   *  week reads as a gap in the trend rather than vanishing and compressing the axis. */
  private buildBars(data: ShippedInvoiceRevenue[]): WeekBar[] {
    const byWeek = _.groupBy(data, (d) => this.dayKey(d.weekStart));

    return _.range(this.weeksBack - 1, -1, -1).map((weeksAgo) => {
      const weekStart = this.mondayOf(new Date());
      weekStart.setDate(weekStart.getDate() - weeksAgo * 7);
      const rows = byWeek[this.dayKey(weekStart)] ?? [];
      return {
        weekStart,
        weekLabel: weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        revenueAmt: _.sumBy(rows, (r) => r.revenueAmt),
        shipmentCount: rows.length,
        unitCount: _.sumBy(rows, (r) => r.unitCount),
      } as WeekBar;
    });
  }

  /** Match the server's WeekStart on calendar day, not on an exact timestamp — a time component
   *  surviving the projection would otherwise drop every row into its own bucket. */
  private dayKey(d: Date) {
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  }

  /** Local-midnight Monday of the week containing d, matching the server's WeekStart. */
  private mondayOf(d: Date) {
    const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    r.setDate(r.getDate() - ((r.getDay() + 6) % 7));
    return r;
  }

  getTitle() {
    return `Weekly Shipment Revenue and Volume - Last ${this.weeksBack} Weeks`;
  }

  renderer(params: AgCartesianSeriesTooltipRendererParams) {
    const d = params.datum as WeekBar;
    return {
      title: `Week of ${d.weekLabel}`,
      content:
        `${UtilFns.fmtCurrency(d.revenueAmt)} on ${d.shipmentCount} shipments` +
        `<br/>${d.unitCount} units`,
    };
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Shipment Revenue");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:G1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:G2");

    worksheet.addRow([]);

    worksheet.addRow(["Week Beginning", "Ship Date", "Account", "Invoice", "Job Order", "Units", "Freight", "Revenue"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 16;
    worksheet.getColumn(2).width = 14;
    worksheet.getColumn(3).width = 32;
    worksheet.getColumn(4).width = 14;
    worksheet.getColumn(5).width = 12;
    worksheet.getColumn(6).width = 10;
    worksheet.getColumn(7).width = 12;
    worksheet.getColumn(8).width = 14;

    this.detailRows.forEach((r) => {
      worksheet.addRow([
        r.weekStart,
        r.shipDate,
        r.accountName,
        r.invoiceHeaderId,
        r.joHeaderId,
        r.unitCount,
        r.freightAmt,
        r.revenueAmt,
      ]);
    });
    worksheet.getColumn(1).numFmt = "mm/dd/yyyy";
    worksheet.getColumn(2).numFmt = "mm/dd/yyyy";
    worksheet.getColumn(7).numFmt = '"$"#,##0.00';
    worksheet.getColumn(8).numFmt = '"$"#,##0.00';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Weekly_Shipment_Revenue_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): ShippedInvoiceRevenue[] {
    return this.detailRows;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "weekStart", headerName: "Week Beginning", flex: 1 },
      { field: "shipDate", headerName: "Ship Date", flex: 1 },
      { field: "accountName", headerName: "Account", flex: 2 },
      { field: "invoiceHeaderId", headerName: "Invoice", flex: 1 },
      { field: "joHeaderId", headerName: "Job Order", flex: 1, type: "leftAligned" },
      { field: "unitCount", headerName: "Units", flex: 1, type: "leftAligned" },
      { field: "freightAmt", headerName: "Freight", flex: 1 },
      { field: "revenueAmt", headerName: "Revenue", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "Weekly_Shipment_Revenue";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
